import type { FunctionComponent } from 'react';
import CTAButton, { CTAColorVariant } from '~/components/shared/CTAButton';
import LinkWrapper from '~/components/shared/LinkWrapper/linkWrapper';
import WmplusLogo from '~/components/shared/svg/WmplusLogo';
import { useMonarchSlotContext } from '@redventures/cohesion-utils-react';
import { useVariantIds } from '~/lib/hooks/useVariantIds';
import Image from 'next-export-optimize-images/image';
import classNames from 'classnames';
import { WALMART_URLS } from '../../../config/urls';
import { useRouter } from 'next/router';

export interface SlimmedNavbarProps {
  CTAButtonText?: string;
  CTAButtonVariant?: CTAColorVariant;
  CTAButtonLink?: string;
  signInButton?: boolean;
}

const DEFAULT_PROPS = {
  CTAButtonText: 'Claim offer now',
  CTAButtonVariant: 'secondary' as CTAColorVariant,
  CTAButtonLink:
    'https://www.walmart.com/?wplusSignupOffer=true&programId=discmemb23',
  signInButton: false,
};

const STUDENT_PROPS = {
  CTAButtonText: 'Claim your free trial',
  CTAButtonVariant: 'secondary' as CTAColorVariant,
  CTAButtonLink: WALMART_URLS.PLUS_STUDENT_SIGNUP,
  signInButton: false,
};

const logoWrapperClasses = classNames(
  'flex mt-1',
  'absolute left-1/2 -translate-x-1/2',
  'md:static md:translate-x-0 md:mr-auto'
);

const headerLinksWrapperClasses = classNames(
  'flex flex-row items-center',
  'gap-0 md:gap-4',
  'text-base font-medium'
);

const signInWrapperClasses = classNames(
  'flex flex-col items-center',
  'gap-0',
  'absolute right-4 top-1/2 -translate-y-1/2',
  'md:flex-row md:gap-[10px] md:static md:translate-y-0'
);

const signInButtonWrapperClasses = classNames(
  'flex flex-col',
  'text-white font-medium text-xs md:text-base'
);

const desktopTextClasses = classNames('font-bold text-base text-white');

const signInButtonImageWrapperClasses = classNames('-mb-0.5');

const ctaClasses = classNames('hidden md:block');

const SlimmedNavbar: FunctionComponent<SlimmedNavbarProps> = (props) => {
  const { data } = useMonarchSlotContext<typeof DEFAULT_PROPS>();
  const { isDarkMode } = useVariantIds();
  const router = useRouter();
  const isStudentPage = router.pathname.startsWith('/student');
  const baseProps = isStudentPage ? STUDENT_PROPS : DEFAULT_PROPS;
  const mergedData = {
    ...baseProps,
    ...data,
    ...props,
  };
  const { CTAButtonText, CTAButtonVariant, CTAButtonLink, signInButton } =
    mergedData;

  const headerClasses = classNames(
    'flex items-center relative justify-center',
    'h-[50px] px-4',
    'md:justify-between desktop:px-6',
    isDarkMode ? 'bg-secondary-dark' : 'bg-digital-walmart-blue'
  );

  const imgPath = `${process.env.baseUrl}/images/sections/hero/`;

  return (
    <header className={headerClasses}>
      <LinkWrapper
        href='https://walmart.com'
        className={logoWrapperClasses}
        segmentTracking={{
          elementName: 'WALMART_LOGO',
          elementPosition: 'NAV',
        }}
      >
        <WmplusLogo alt='Walmart+ Logo' width={94} height={32} />
      </LinkWrapper>
      <div className={headerLinksWrapperClasses}>
        <div>
          <CTAButton
            className={ctaClasses}
            variant={CTAButtonVariant}
            size='thin'
            elementPosition='NAVBAR'
            elementType='SIGN_UP_CTA_PRIMARY'
            href={CTAButtonLink}
          >
            {CTAButtonText}
          </CTAButton>
        </div>
        {signInButton && (
          <div className={signInWrapperClasses}>
            <LinkWrapper
              href={CTAButtonLink}
              className={signInButtonImageWrapperClasses}
              segmentTracking={{
                elementName: 'SIGN_IN_BUTTON',
                elementPosition: 'NAV',
              }}
            >
              <Image
                src={`${imgPath}person-white.svg`}
                alt='Navbar person image'
                width={16}
                height={18}
                className='w-auto'
                priority={true}
              />
            </LinkWrapper>
            <div className={signInButtonWrapperClasses}>
              <LinkWrapper
                href={CTAButtonLink}
                className={desktopTextClasses}
                segmentTracking={{
                  elementName: 'SIGN_IN_BUTTON',
                  elementPosition: 'NAV',
                }}
              >
                Sign In
              </LinkWrapper>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default SlimmedNavbar;
